import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ApiError, get, post } from '../../services/api';
import { RequireUser } from '../../components/RequireUser';
import { ErrorBox, Loading, PageHeader } from '../../components/Ui';
import { Icon } from '../../components/Icon';
import { useAsync, useTitle } from '../../hooks';
import { fullDate } from '../../shared/utils/format';

interface Lookup {
  userCode: string;
  client: string;
  expiresAt: string;
}

/** Where a command-line client sends a person to say yes or no. */
export function Device() {
  useTitle('Connect a device');
  return <RequireUser>{(user) => <DeviceForm username={user.username} />}</RequireUser>;
}

function DeviceForm({ username }: { username: string }) {
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get('code') ?? '');
  const [submitted, setSubmitted] = useState(params.get('code') ?? '');

  return (
    <div className="container narrow stack">
      <PageHeader
        title="Connect a device"
        description={
          <>
            Signed in as <strong>{username}</strong>.
          </>
        }
      />
      {submitted ? (
        <Decision code={submitted} onBack={() => setSubmitted('')} />
      ) : (
        <form
          className="card card-pad"
          onSubmit={(e) => {
            e.preventDefault();
            setSubmitted(code.trim());
          }}
        >
          <p className="muted">Enter the code from your terminal to review the connection request.</p>
          <div className="field">
            <label htmlFor="code">Code shown on your device</label>
            <input
              id="code"
              className="input mono"
              autoFocus
              autoComplete="off"
              placeholder="ABCD-EFGH"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </div>
          <button className="btn primary" disabled={!code.trim()}>
            Continue
            <Icon name="arrow" />
          </button>
        </form>
      )}
    </div>
  );
}

function Decision({ code, onBack }: { code: string; onBack: () => void }) {
  const lookup = useAsync(() => get<Lookup>(`/auth/device/lookup?code=${encodeURIComponent(code)}`), [code]);
  const [write, setWrite] = useState(true);
  const [outcome, setOutcome] = useState<'approved' | 'denied'>();
  const [error, setError] = useState<ApiError>();
  const [busy, setBusy] = useState(false);

  const decide = async (approve: boolean) => {
    setBusy(true);
    setError(undefined);
    try {
      if (approve)
        await post('/auth/device/approve', {
          userCode: code,
          scopes: write ? ['repo:read', 'repo:write'] : ['repo:read'],
        });
      else await post('/auth/device/deny', { userCode: code });
      setOutcome(approve ? 'approved' : 'denied');
    } catch (e) {
      setError(e instanceof ApiError ? e : new ApiError(0, 'error', 'Something went wrong.'));
    } finally {
      setBusy(false);
    }
  };

  if (outcome) {
    return (
      <div className="card card-pad stack">
        <div className={`alert ${outcome === 'approved' ? 'ok' : 'warn'}`} role="status">
          {outcome === 'approved'
            ? 'Approved. Go back to the terminal that showed this code: it finishes signing in on its own.'
            : 'Denied. Nothing was connected.'}
        </div>
      </div>
    );
  }
  if (lookup.loading && !lookup.data) return <Loading />;
  if (lookup.error) {
    return (
      <div className="stack">
        <ErrorBox
          error={
            lookup.error.status === 404
              ? 'That code is not valid, or it has expired. Start again from your device.'
              : lookup.error
          }
          retry={lookup.error.status === 404 ? undefined : lookup.reload}
        />
        <button className="btn" onClick={onBack}>
          Enter a different code
        </button>
      </div>
    );
  }
  const data = lookup.data!;

  return (
    <div className="card card-pad stack">
      <p style={{ margin: 0 }}>
        <strong>{data.client}</strong> wants to access your Library account.
      </p>
      <div>
        <div className="muted" style={{ marginBottom: 4 }}>
          Check that this code matches the one on your device:
        </div>
        <span className="code-big" aria-label={`Code ${data.userCode.split('').join(' ')}`}>
          {data.userCode}
        </span>
      </div>
      <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
        <legend className="label" style={{ marginBottom: 6 }}>
          It will be able to
        </legend>
        <label className="check">
          <input type="checkbox" checked disabled /> <span>Read your wikis, including private ones</span>
        </label>
        <label className="check">
          <input type="checkbox" checked={write} onChange={(e) => setWrite(e.target.checked)} />{' '}
          <span>Push changes and create wikis</span>
        </label>
      </fieldset>
      <p className="hint">This request expires {fullDate(data.expiresAt)}.</p>
      <p className="hint" style={{ margin: 0 }}>
        It will not be able to change your password, manage tokens, delete wikis or change who has access.
        Only approve a code you just asked for.
      </p>
      {error && <ErrorBox error={error} />}
      <div className="row">
        <button className="btn primary" disabled={busy} onClick={() => decide(true)}>
          Approve
        </button>
        <button className="btn" disabled={busy} onClick={() => decide(false)}>
          Deny
        </button>
      </div>
    </div>
  );
}
